// Bet Cracker — Audio Engine.
//
// Synthesized sound effects via Web Audio API (zero external assets).
// • All playback requires a prior user gesture (autoplay policy).
// • Master toggle (`sfxEnabled`) and ambient toggle (`ambientEnabled`)
//   are persisted to localStorage and shared across all components
//   via the `audioPrefs$` subscribable store.
// • Public API: `playSfx(name)`, `toggleSfx()`, `toggleAmbient()`,
//   `getPrefs()`, `subscribe(fn)`.

const STORAGE_KEY = "bc_audio_prefs_v1";

const DEFAULTS = {
  sfxEnabled: false,     // opt-in — user enables it the first time
  ambientEnabled: false,
  volume: 0.35,
};

let prefs = loadPrefs();
const listeners = new Set();
let ctx = null;
let masterGain = null;
let ambientNodes = null;

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULTS };
  }
}

function persist() {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
  listeners.forEach((fn) => { try { fn(prefs); } catch { /* ignore */ } });
}

function ensureCtx() {
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    masterGain = ctx.createGain();
    masterGain.gain.value = prefs.volume;
    masterGain.connect(ctx.destination);
    return ctx;
  } catch {
    return null;
  }
}

// ─── Synthesizer primitives ───────────────────────────────────────────
function tone({ freq = 440, dur = 0.2, type = "sine", vol = 0.6, attack = 0.005, release = 0.08, detune = 0 }) {
  if (!ensureCtx()) return;
  const t = ctx.currentTime;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  osc.detune.value = detune;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur + release);
  osc.connect(g).connect(masterGain);
  osc.start(t);
  osc.stop(t + dur + release + 0.02);
}

function noiseBurst({ dur = 0.15, vol = 0.6, filterFreq = 2000, q = 1.5, sweep = true }) {
  if (!ensureCtx()) return;
  const t = ctx.currentTime;
  const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * dur), ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.9;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.Q.value = q;
  filter.frequency.setValueAtTime(filterFreq, t);
  if (sweep) filter.frequency.exponentialRampToValueAtTime(filterFreq * 0.25, t + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(vol, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(filter).connect(g).connect(masterGain);
  src.start(t);
  src.stop(t + dur + 0.02);
}

// ─── SFX catalog ──────────────────────────────────────────────────────
const SFX = {
  // Katana slash (Samurai COLPISCI! button): noise burst swept high→low
  katana: () => {
    noiseBurst({ dur: 0.32, vol: 0.55, filterFreq: 5000, q: 2, sweep: true });
    // Metallic ring tail
    setTimeout(() => tone({ freq: 1600, dur: 0.15, type: "triangle", vol: 0.25, release: 0.25 }), 60);
  },
  // Deep gong for Bushido Coherence activation
  gong: () => {
    tone({ freq: 70,  dur: 1.1, type: "sine",     vol: 0.5,  release: 1.5 });
    tone({ freq: 140, dur: 0.9, type: "triangle", vol: 0.3,  release: 1.1 });
    tone({ freq: 210, dur: 0.6, type: "sine",     vol: 0.15, release: 0.8, detune: -8 });
  },
  // Coin drop (save bet)
  coin: () => {
    tone({ freq: 1200, dur: 0.08, type: "triangle", vol: 0.4 });
    setTimeout(() => tone({ freq: 880, dur: 0.08, type: "triangle", vol: 0.35 }), 90);
    setTimeout(() => tone({ freq: 1320, dur: 0.12, type: "triangle", vol: 0.28, release: 0.2 }), 180);
  },
  // Winning chime (3-note rising)
  win: () => {
    [523.25, 659.25, 783.99].forEach((f, i) => {
      setTimeout(() => tone({ freq: f, dur: 0.18, type: "triangle", vol: 0.4, release: 0.3 }), i * 110);
    });
  },
  // Loss thud
  lose: () => {
    tone({ freq: 180, dur: 0.22, type: "sawtooth", vol: 0.35, release: 0.4 });
    setTimeout(() => tone({ freq: 110, dur: 0.3, type: "sine", vol: 0.25, release: 0.4 }), 80);
  },
  // Wood click for tab switches
  click: () => {
    tone({ freq: 2400, dur: 0.02, type: "square", vol: 0.2, attack: 0.001, release: 0.04 });
    tone({ freq: 800, dur: 0.03, type: "triangle", vol: 0.15, attack: 0.001, release: 0.05 });
  },
  // Tick for sliders (very short)
  tick: () => {
    tone({ freq: 3200, dur: 0.01, type: "square", vol: 0.08, attack: 0.001, release: 0.02 });
  },
  // Warning beep for TRAP hover
  warn: () => {
    tone({ freq: 320, dur: 0.08, type: "square", vol: 0.22, release: 0.1 });
    setTimeout(() => tone({ freq: 280, dur: 0.08, type: "square", vol: 0.18, release: 0.1 }), 90);
  },
};

// ─── Ambient Dojo Soundscape ──────────────────────────────────────────
// Pink-ish filtered noise at very low volume + occasional rare bell tones.
function startAmbient() {
  if (!ensureCtx() || ambientNodes) return;
  const t = ctx.currentTime;

  // Pink noise as wind bed
  const bufSize = 2 * ctx.sampleRate;
  const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let b0 = 0, b1 = 0, b2 = 0;
  for (let i = 0; i < bufSize; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.997 * b0 + 0.0296 * white;
    b1 = 0.985 * b1 + 0.0540 * white;
    b2 = 0.950 * b2 + 0.180  * white;
    d[i] = (b0 + b1 + b2) * 0.28;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buf; noise.loop = true;

  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass"; lp.frequency.value = 420; lp.Q.value = 0.6;

  // Slow LFO on gain for "breathing" wind
  const wind = ctx.createGain();
  wind.gain.value = 0.0001;
  wind.gain.setTargetAtTime(0.06, t + 1.0, 4.0);

  const lfo = ctx.createOscillator();
  lfo.type = "sine"; lfo.frequency.value = 0.08;
  const lfoGain = ctx.createGain(); lfoGain.gain.value = 0.025;
  lfo.connect(lfoGain).connect(wind.gain);
  lfo.start();

  noise.connect(lp).connect(wind).connect(masterGain);
  noise.start();

  // Sporadic deep taiko-like hits every 18-42 seconds
  const scheduleTaiko = () => {
    if (!ambientNodes) return;
    const wait = 18000 + Math.random() * 24000;
    ambientNodes.taikoTimer = setTimeout(() => {
      if (!ambientNodes) return;
      tone({ freq: 85, dur: 0.35, type: "sine", vol: 0.18, release: 0.6 });
      scheduleTaiko();
    }, wait);
  };

  ambientNodes = { noise, lp, wind, lfo, lfoGain, taikoTimer: null };
  scheduleTaiko();
}

function stopAmbient() {
  if (!ambientNodes) return;
  try {
    const { noise, wind, lfo, taikoTimer } = ambientNodes;
    if (taikoTimer) clearTimeout(taikoTimer);
    wind.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.3);
    setTimeout(() => {
      try { noise.stop(); } catch { /* ignore */ }
      try { lfo.stop(); } catch { /* ignore */ }
    }, 600);
  } catch { /* ignore */ }
  ambientNodes = null;
}

// ─── Public API ───────────────────────────────────────────────────────
export function playSfx(name) {
  if (!prefs.sfxEnabled) return;
  if (!ensureCtx()) return;
  // Resume context if suspended (first gesture)
  if (ctx.state === "suspended") { try { ctx.resume(); } catch { /* ignore */ } }
  const fn = SFX[name];
  if (typeof fn === "function") {
    try { fn(); } catch { /* ignore */ }
  }
}

export function toggleSfx() {
  prefs.sfxEnabled = !prefs.sfxEnabled;
  persist();
  if (prefs.sfxEnabled) {
    ensureCtx();
    if (ctx?.state === "suspended") { try { ctx.resume(); } catch { /* ignore */ } }
    // Quick confirmation sound
    setTimeout(() => playSfx("click"), 50);
  }
}

export function toggleAmbient() {
  prefs.ambientEnabled = !prefs.ambientEnabled;
  persist();
  if (prefs.ambientEnabled) {
    ensureCtx();
    if (ctx?.state === "suspended") { try { ctx.resume(); } catch { /* ignore */ } }
    startAmbient();
  } else {
    stopAmbient();
  }
}

export function setVolume(v) {
  prefs.volume = Math.max(0, Math.min(1, v));
  if (masterGain) masterGain.gain.value = prefs.volume;
  persist();
}

export function getPrefs() { return { ...prefs }; }

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Auto-start ambient if user had it enabled last session, AFTER first interaction
if (typeof window !== "undefined") {
  const kick = () => {
    if (prefs.ambientEnabled) {
      ensureCtx();
      if (ctx?.state === "suspended") { try { ctx.resume(); } catch { /* ignore */ } }
      startAmbient();
    }
    window.removeEventListener("click", kick);
    window.removeEventListener("keydown", kick);
  };
  window.addEventListener("click", kick, { once: true });
  window.addEventListener("keydown", kick, { once: true });
}
